import { Fragment, PureComponent } from "react";

import Spinner from "../../../common/components/Spinner/Spinner";
import { trackCadDataClick, trackCadDownload } from "../../../feature/Analytics/ga/ga";
import { IconType } from "../../../common/components/icon/IconType";
import { type WithTranslation, withTranslation as withTranslationBase } from "react-i18next";
import { LINEAR_CONFIGURATION_DISCLAIMER } from "../../../utils/url/staticUrls";
import { getCadFormatCookie, setCadFormatCookie } from "./cadFormatCookie";
import { SimpleSelect } from "~/common/components/Select/SimpleSelect";
import { Button } from "~/common/components/Button/Button";
import { Icon } from "~/common/components/icon/Icon";
import { fetchCadDownloadUrl } from "~/ProductDetails/components/Cad/api/fetchCadDownloadUrl";
import { Alert } from "~/common/m3/Alert/Alert";

interface CadDownloadProps extends WithTranslation {
  selectionPath?: string;
  partId?: string;
  productCode: string;
  productDesignation: string;
  cadFormats?: {
    value: number;
    label: string;
  }[];
}

interface CadDownloadState {
  inFlight: boolean;
  selectedFormatId?: number;
  error: boolean;
  formats?: {
    value: number;
    label: string;
  }[];
  link: string;
  hasClickedDownload: boolean;
  showFullscreenModal: boolean;
}

// TODO - Change into functional component
class CadDownload extends PureComponent<CadDownloadProps, CadDownloadState> {
  constructor(props: CadDownloadProps) {
    super(props);

    this.state = {
      inFlight: false,
      error: false,
      link: "",
      showFullscreenModal: false,
      hasClickedDownload: false,
    };
  }

  componentDidMount() {
    this.getDownloadOptions();
  }

  componentDidUpdate(prevProps: CadDownloadProps) {
    if (this.props.selectionPath !== prevProps.selectionPath) {
      this.setState(
        {
          ...this.state,
          formats: undefined,
        },
        () => this.getDownloadOptions(),
      );
    }
  }

  downloadCadFile = () => {
    if (!this.state.hasClickedDownload) {
      this.setState({ ...this.state, hasClickedDownload: true });
    }
    if (this.state.selectedFormatId) {
      const { selectionPath, partId } = this.props;

      this.setState({ ...this.state, inFlight: true });
      fetchCadDownloadUrl({
        selectionPath: selectionPath!,
        partId: partId!,
        cadFormatId: this.state.selectedFormatId,
      })
        .then((link) => {
          if (link.startsWith('"')) {
            link = link.substring(1, link.length - 1);
          }
          setCadFormatCookie(this.state.selectedFormatId?.toString() ?? "");
          this.setState({
            ...this.state,
            inFlight: false,
            link: link,
          });
          window.open(link, "_self");
        })
        .catch((error) => {
          this.setState({
            ...this.state,
            inFlight: false,
            error: true,
          });
        });

      const format = this.state.formats!.filter((el) => el.value === this.state.selectedFormatId)[0]!.label;
      trackCadDataClick(format);
      trackCadDownload(
        this.props.productDesignation,
        this.props.productCode,
        format,
        !!window.SCHA.visitedSalesPartner,
        window.SCHA.visitedSalesPartner?.name ?? undefined,
      );
    }
  };

  closeModal = () => this.setState({ ...this.state, showFullscreenModal: false });

  openModal = () => this.setState({ ...this.state, showFullscreenModal: true });

  getDownloadOptions = () => {
    if (this.state.formats) {
      return;
    }

    this.setState({
      ...this.state,
      inFlight: true,
    });

    const { cadFormats } = this.props;
    const cookieId = parseInt(getCadFormatCookie());

    const findFormat = cadFormats!.find((item) => item.value === cookieId);
    this.setState({
      ...this.state,
      inFlight: false,
      selectedFormatId: findFormat ? findFormat.value : undefined,
      formats: cadFormats,
    });
    if (findFormat) {
      this.updateCadFormat(findFormat.value);
    }
  };

  updateCadFormat = (formatId?: number) => {
    if (formatId === undefined) {
      return;
    }

    this.setState({
      ...this.state,
      selectedFormatId: formatId,
      inFlight: false,
      error: false,
      link: "",
    });
  };

  render() {
    const { t, partId } = this.props;

    if (!partId) {
      return null;
    }

    const { inFlight, formats, error, selectedFormatId, hasClickedDownload } = this.state;

    const isFusion =
      selectedFormatId !== undefined &&
      selectedFormatId === formats?.find((format) => format.label === "FUSION 360")?.value;

    return (
      <Fragment>
        {inFlight && !formats && <Spinner className="mt-3 mb-3" />}
        {!formats && error && <Alert severity="error">{t("cadDownload.error")}</Alert>}
        {formats && (
          <div className="scha__cad-download__container">
            <div className="scha__cad-download__title">{t("cadDownload.title")}</div>
            <SimpleSelect
              className={"scha__cad-download__dropdown"}
              label={t("cadDownload.placeholder")}
              values={formats}
              value={selectedFormatId ?? ""}
              fullWidth={true}
              error={hasClickedDownload && !selectedFormatId}
              onChange={(e) => this.updateCadFormat(e.target.value === undefined ? undefined : +e.target.value)}
            />
            {inFlight && <Spinner />}
            {error && <Alert severity="error">{t("cadDownload.error")}</Alert>}
            <Button
              disabled={inFlight}
              onClick={this.downloadCadFile}
              startIcon={<Icon type={IconType.Download} />}
              fullWidth={true}
            >
              {isFusion ? t("cadDownload.btn.downloadFusion") : t("cadDownload.btn.download")}
            </Button>
            <Alert
              severity={"info"}
              className="scha__cad-download__disclaimer"
              action={
                <a target={"_blank"} href={LINEAR_CONFIGURATION_DISCLAIMER}>
                  {t("newsletter.consent.seemore")}
                </a>
              }
            >
              {t("cadDownload.disclaimer") + " "}
            </Alert>
          </div>
        )}
      </Fragment>
    );
  }
}

const translated = withTranslationBase()(CadDownload);
export default translated;
